<template>
  <Popover placement="bottom-end" :distance="6">
    <template #trigger="{ toggleDropdown }">
      <LeadButton
        variant="ghost"
        size="sm"
        :icon="sortDirectionIcon"
        :data-sort-order="internalValue.key"
        data-testid="orders-sort-button"
        :aria-label="$t('sort.direction')"
        @click="toggleDropdown">
        <template v-if="currentSortLabel">{{ currentSortLabel }}</template>
      </LeadButton>
    </template>
    <template #dropdown>
      <div class="tw:p-3" style="min-width: 220px">
        <LeadRadioGroup
          :model-value="internalValue.key ?? ''"
          :options="radioOptions"
          @update:model-value="(key: string | number) => (internalValue = { ...internalValue, key: String(key) })" />
        <div class="tw:my-3 tw:h-px tw:bg-border-secondary" />
        <LeadSegmentedControl
          :model-value="internalValue.direction"
          :label="$t('sort.direction')"
          size="sm"
          :options="directionOptions"
          class="tw:w-full tw:flex tw:flex-col"
          @update:model-value="(dir) => (internalValue = { ...internalValue, direction: dir as 'asc' | 'desc' })" />
      </div>
    </template>
  </Popover>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconSortAscending, IconSortDescending } from '@tabler/icons-vue';
import Popover from '@/components/common/Popover.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadSegmentedControl from '@/components/lead-design/LeadSegmentedControl.vue';
import LeadRadioGroup from '@/components/lead-design/LeadRadioGroup.vue';

export interface SortState {
  key: string | undefined;
  direction: 'asc' | 'desc';
}

export interface SortOption {
  key: string;
  label: string;
}

export interface Props {
  modelValue: SortState;
  sortOptions: SortOption[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: SortState): void;
}>();

/* --------------------------------- COMPUTED ------------------------------- */

const internalValue = computed({
  get() {
    return props.modelValue ?? { key: undefined, direction: 'asc' };
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const sortDirectionIcon = computed(() =>
  internalValue.value.direction === 'asc' ? IconSortAscending : IconSortDescending
);

const currentSortLabel = computed(
  () => props.sortOptions.find((option) => option.key === internalValue.value.key)?.label ?? ''
);

const radioOptions = computed(() => props.sortOptions.map((o) => ({ value: o.key, label: o.label })));

// A-Z / Z-A toggle. Labels double as the segmented control's visible text.
const directionOptions = [
  { value: 'asc' as const, label: 'A-Z', icon: IconSortAscending, ariaLabel: 'Ascending' },
  { value: 'desc' as const, label: 'Z-A', icon: IconSortDescending, ariaLabel: 'Descending' },
];
</script>
